import styled, { css } from 'styled-components'
import Image from 'next/image'
import { app } from 'src/configuration'

import Icon from '../Icon'
import { mq, sizes } from '../../theme'
import A from '../A'

const publicPathPrefix = app.ASSET_PREFIX

export enum ContentCardVariant {
  Default = 'default',
  Article = 'article',
}

export const Background = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-areas: '. image';
  position: absolute;
  height: 100%;
  width: 100%;

  @media ${mq(sizes.desktop)} {
    grid-template-columns: auto;
    grid-template-rows: 1fr 1fr;
    grid-template-areas:
      '.'
      'image';
  }
`

export const ButtonsContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  flex-flow: row wrap;
  margin: auto 0 1rem;
  place-self: end;
  gap: 1rem;

  & > * {
    text-align: center;
  }

  & span {
    ${({ theme }) => theme.textStyles.text_bold_condensed_12};
  }

  @media ${mq(sizes.tablet)} {
    margin-bottom: 0;
  }

  @media ${mq(sizes.boxBigger)} {
    //flex-flow: row nowrap;
  }
`

const cardWrapperStyles = css<{ forceLandscape?: boolean }>`
  display: flex;
  position: relative;
  border-radius: 2rem;
  overflow: hidden;
  height: 25rem;
  width: 100%;

  @media ${mq(sizes.desktop)} {
    width: 100%;
    height: 43rem;
  }

  ${({ forceLandscape }) =>
    forceLandscape &&
    css`
      @media ${mq(sizes.desktop)} {
        height: 41.5rem;
      }

      ${Background} {
        @media ${mq(sizes.desktop)} {
          grid-template-rows: auto;
          grid-template-columns: 1fr 1fr;
          grid-template-areas: '. image .';
        }
      }

      ${ButtonsContainer} {
        margin-top: 1rem;
        @media ${mq(sizes.desktop)} {
          justify-content: flex-start;
        }
      }
    `}
`
export const Card = styled(A)<{ forceLandscape?: boolean }>`
  ${cardWrapperStyles}
`

export const BgTexture = styled.div`
  background-image: url('${publicPathPrefix}/images/Textures/grudge.png');
  background-repeat: repeat;
  display: flex;
  width: 100%;
  height: 100%;
  opacity: 0.4;
`

export const TextureSection = styled.div<{
  backgroundColor?: string
}>`
  display: flex;
  position: absolute;
  inset: 0;
  background: transparent;
  height: 100%;
  width: 100%;
  background-color: ${({ backgroundColor, theme }) =>
    backgroundColor ?? theme.colors.redMandy};
`

export const ImageSection = styled.div`
  display: flex;
  position: relative;
  height: 100%;
  width: 100%;
  grid-area: image;
`

export const LocationWrapper = styled.div`
  display: none;
  position: absolute;
  top: 0;
  right: 0;
  border-bottom-left-radius: 2rem;
  background-color: ${({ theme }) => theme.colors.pureWhite};
  align-items: center;
  justify-content: center;
  width: 8rem;
  height: 8rem;
  z-index: 1;
  @media ${mq(sizes.tablet)} {
    display: flex;
  }
`

export const LocationImage = styled(Image)`
  padding: 0.5rem 1rem;
  width: 80% !important;
  height: auto !important;
  min-height: auto !important;
  min-width: auto !important;
`

export const ImageWrapper = styled.div`
  position: relative;
`

export const CImage = styled(Image)<{ $variant: ContentCardVariant }>`
  ${({ $variant }) =>
    $variant === ContentCardVariant.Article
      ? css`
          position: relative !important;
          object-fit: contain !important;
        `
      : css``}
`

export const Foreground = styled.div<{ has3rows?: boolean }>`
  display: grid;
  grid-template-rows: ${({ has3rows }) =>
    has3rows ? 'min-content min-content 1fr' : 'min-content min-content 1fr'};
  position: absolute;
  flex-direction: column;
  width: 100%;
  height: 100%;
  padding: 2.5rem;

  @media ${mq(sizes.desktop)} {
    padding: 3rem;
  }
`

export const FirstHighlight = styled.div`
  ${({ theme }) => theme.textStyles.text_bold_condensed_16}
  padding: 0rem;
  margin: 0;
  @media ${mq(sizes.desktop)} {
    ${({ theme }) => theme.textStyles.text_bold_condensed_12}
  }
`

export const SecondHighlight = styled.div`
  ${({ theme }) => theme.textStyles.text_bold_condensed_18}
  padding: 0;
  margin: 0;
  text-transform: inherit;
`

export const ThirdHighlight = styled.div``

export const Duration = styled.div`
  ${({ theme }) => theme.textStyles.text_bold_condensed_16}
  display: flex;
  flex-direction: row;
  align-items: center;
  align-self: flex-start;
  padding: 0.3rem 0.7rem;
  margin: 0.1rem 0 0 0;
  width: min-content;
  background: ${({ theme }) => theme.colors.pureWhite80};

  @media ${mq(sizes.desktop)} {
    margin: 0.2rem 0 0 0;
    ${({ theme }) => theme.textStyles.text_bold_condensed_16}
  }
`

export const Tag = styled(Duration)`
  ${({ theme }) => theme.textStyles.text_18}
  width: max-content;
  color: ${({ theme }) => theme.colors.pureWhite};
  text-transform: capitalize;
  background: ${({ theme }) => theme.colors.silkBlue};
  max-width: 31rem;
  align-items: flex-start;

  & p {
    margin: 0;
  }
`

export const Date = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  align-self: flex-start;
  padding: 0.3rem 0.7rem;
  margin: 0.1rem 0 0 0;
  width: min-content;
  ${({ theme }) => theme.textStyles.text_18}
  width: max-content;
  color: ${({ theme }) => theme.colors.pureWhite};
  background: ${({ theme }) => theme.colors.silkBlue};
  max-width: 31rem;
  align-items: flex-start;

  & p {
    margin: 0;
  }

  white-space: pre-wrap;
  @media ${mq(sizes.tablet)} {
    white-space: normal;
  }
`

export const Location = styled(Tag)``

export const ClockIcon = styled(Icon)`
  display: block;
  width: 2.2rem;
  height: 2.2rem;
  margin-right: 0.75rem;
`

export const SportIcon = styled(ClockIcon)`
  display: flex;
  align-items: center;
  width: 1.8rem;
  height: 1.8rem;
  color: ${({ theme }) => theme.colors.pureWhite};
  padding: 0.5rem 0 0 0;

  & svg {
    width: 1.8rem;
    height: 1.8rem;
  }
`

export const SearchIcon = styled(SportIcon)``
